import React from "react";
import { InlineMath } from "react-katex";

/**
 * Diagram: okrąg + kąt środkowy α_sr (zielony) i wpisany α_w (fiolet) oparty na tym samym łuku.
 * Podpisy (α, subskrypty) oraz literki punktów są pomarańczowe.
 */
export default function CircleAnglesDiagram({
  centralDeg,
  size = 250,
  circleR = 94,
  showValues = true,
  // nowe: możliwość nadpisania wartości w etykietach
  centralLabel,
  inscribedLabel,
  centralTone = "given",
  inscribedTone = "unknown",
}) {
  const cx = size / 2;
  const cy = size / 2;

  // paleta
  const COLORS = {
    accent: "#f59e0b",
    p1: "#a855f7",
    p2: "#f59e0b",
    unknown: "#a855f7",
    base: "currentColor",
  };
  const centralColor = centralTone === "unknown" ? COLORS.unknown : COLORS.p2;
  const inscribedColor = inscribedTone === "given" ? COLORS.accent : COLORS.p1;

  // wartości
  const C = Math.max(5, Math.min(355, Number(centralDeg) || 60));
  const W = C / 2;

  // końce łuku środkowego: -C/2, +C/2
  const a1 = (-C / 2) * (Math.PI / 180);
  const a2 = (C / 2) * (Math.PI / 180);
  const amid = (a1 + a2) / 2;

  // punkty na okręgu
  const Ax = cx + circleR * Math.cos(a1);
  const Ay = cy + circleR * Math.sin(a1);
  const Bx = cx + circleR * Math.cos(a2);
  const By = cy + circleR * Math.sin(a2);

  // wierzchołek kąta wpisanego (po lewej) – P
  const Vx = cx - circleR;
  const Vy = cy;

  // dwusieczna kąta wpisanego
  const aV1 = Math.atan2(Ay - Vy, Ax - Vx);
  const aV2 = Math.atan2(By - Vy, Bx - Vx);
  const amidInscr = (aV1 + aV2) / 2;

  // różnica przy wierzchołku (do rysowania łuku wpisanego)
  let dV = ((aV2 - aV1) * 180) / Math.PI;
  while (dV <= -180) dV += 360;
  while (dV > 180) dV -= 360;
  const absDV = Math.abs(dV);

  // --- ŁUKI (mniejsze, by zostawić miejsce na etykiety) ---
  const centralArcR = 25;
  const inscribedArcR = 25;

  const pCentralStart = {
    x: cx + centralArcR * Math.cos(a1),
    y: cy + centralArcR * Math.sin(a1),
  };
  const pCentralEnd = {
    x: cx + centralArcR * Math.cos(a2),
    y: cy + centralArcR * Math.sin(a2),
  };

  const pInscribedStart = {
    x: Vx + inscribedArcR * Math.cos(aV1),
    y: Vy + inscribedArcR * Math.sin(aV1),
  };
  const pInscribedEnd = {
    x: Vx + inscribedArcR * Math.cos(aV2),
    y: Vy + inscribedArcR * Math.sin(aV2),
  };

  // promienie (kąt środkowy)
  const rad1 = {
    x: cx + circleR * Math.cos(a1),
    y: cy + circleR * Math.sin(a1),
  };
  const rad2 = {
    x: cx + circleR * Math.cos(a2),
    y: cy + circleR * Math.sin(a2),
  };

  // format stopni
  const fmt = (x) => Number(x).toFixed(2).replace(/\.?0+$/, "");

  // --- Etykiety: poza łukiem, z niewielkim przesunięciem w prawo ---
  const RIGHT_PAD = 1;
  const centralLabelR = centralArcR + 5;
  const labelCentralX = cx + centralLabelR * Math.cos(amid) + RIGHT_PAD;
  const labelCentralY = cy + centralLabelR * Math.sin(amid);

  const inscribedLabelR = inscribedArcR + 10;
  const labelInscrX = Vx + inscribedLabelR * Math.cos(amidInscr) + RIGHT_PAD;
  const labelInscrY = Vy + inscribedLabelR * Math.sin(amidInscr);

  // renderer etykiety k?ta: dana warto?? bez symbolu, niewiadoma jako alpha z indeksem
  const AngleLabel = ({ x, y, sub, value, color = COLORS.accent }) => {
    const math = value ? value : `\\alpha_{${sub}}`;

    return (
      <foreignObject x={x} y={y - 12} width="74" height="26">
        <div style={{ color, fontSize: 14, lineHeight: 1.2 }}>
          <InlineMath math={math} />
        </div>
      </foreignObject>
    );
  };
  // pomocnicze: pozycja etykiety punktu lekko poza okręgiem
  const placeOutside = (px, py, offset = 14) => {
    const vx = px - cx;
    const vy = py - cy;
    const len = Math.hypot(vx, vy) || 1;
    const ux = vx / len;
    const uy = vy / len;
    const x = px + offset * ux;
    const y = py + offset * uy;
    let anchor = "middle";
    if (ux > 0.35) anchor = "start";
    else if (ux < -0.35) anchor = "end";
    return { x, y, anchor };
  };
  const Apos = placeOutside(Ax, Ay);
  const Bpos = placeOutside(Bx, By);
  const Ppos = placeOutside(Vx, Vy);
  const Opos = { x: cx, y: cy - 10, anchor: "middle" };

  const PointLabel = ({ x, y, math, anchor = "middle" }) => {
    const boxX = anchor === "start" ? x : anchor === "end" ? x - 36 : x - 18;
    const textAlign = anchor === "start" ? "left" : anchor === "end" ? "right" : "center";

    return (
      <foreignObject x={boxX} y={y - 13} width="36" height="26">
        <div style={{ color: COLORS.base, fontSize: 15, textAlign, lineHeight: 1.2 }}>
          <InlineMath math={math} />
        </div>
      </foreignObject>
    );
  };

  // wartości do etykiet (nadpisywalne z propsów)
  const centralValue =
    typeof centralLabel === "string"
      ? centralLabel || undefined
      : showValues && centralTone !== "unknown"
      ? `${fmt(C)}^{\\circ}`
      : undefined;

  const inscribedValue =
    typeof inscribedLabel === "string"
      ? inscribedLabel || undefined
      : showValues && inscribedTone !== "unknown"
      ? `${fmt(W)}^{\\circ}`
      : undefined;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* okrąg */}
      <circle
        cx={cx}
        cy={cy}
        r={circleR}
        fill="none"
        stroke={COLORS.base}
        strokeWidth="2"
      />

      {/* ramiona kąta środkowego */}
      <line
        x1={cx}
        y1={cy}
        x2={rad1.x}
        y2={rad1.y}
        stroke={COLORS.base}
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      <line
        x1={cx}
        y1={cy}
        x2={rad2.x}
        y2={rad2.y}
        stroke={COLORS.base}
        strokeWidth="2.2"
        strokeLinecap="round"
      />

      {/* ramiona kąta wpisanego */}
      <line
        x1={Vx}
        y1={Vy}
        x2={Ax}
        y2={Ay}
        stroke={COLORS.base}
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      <line
        x1={Vx}
        y1={Vy}
        x2={Bx}
        y2={By}
        stroke={COLORS.base}
        strokeWidth="2.2"
        strokeLinecap="round"
      />

      {/* łuk środkowy */}
      <path
        d={`M ${pCentralStart.x} ${pCentralStart.y}
            A ${centralArcR} ${centralArcR} 0 ${C > 180 ? 1 : 0} 1
              ${pCentralEnd.x} ${pCentralEnd.y}`}
        fill="none"
        stroke={centralColor}
        strokeWidth="3"
        strokeLinecap="round"
      />

      {/* łuk wpisany */}
      <path
        d={`M ${pInscribedStart.x} ${pInscribedStart.y}
            A ${inscribedArcR} ${inscribedArcR} 0 ${absDV > 180 ? 1 : 0} 1
              ${pInscribedEnd.x} ${pInscribedEnd.y}`}
        fill="none"
        stroke={inscribedColor}
        strokeWidth="3"
        strokeLinecap="round"
      />

      {/* punkty */}
      <circle cx={Ax} cy={Ay} r="4.2" fill={COLORS.base} />
      <circle cx={Bx} cy={By} r="4.2" fill={COLORS.base} />
      <circle cx={Vx} cy={Vy} r="4.2" fill={COLORS.base} />
      <circle cx={cx} cy={cy} r="3.2" fill={COLORS.base} />

      {/* literki punktow: KaTeX + kolor z motywu */}
      <PointLabel x={Apos.x} y={Apos.y} math={"A"} anchor={Apos.anchor} />
      <PointLabel x={Bpos.x} y={Bpos.y} math={"B"} anchor={Bpos.anchor} />
      <PointLabel x={Ppos.x} y={Ppos.y} math={"P"} anchor={Ppos.anchor} />
      <PointLabel x={Opos.x} y={Opos.y} math={"O"} anchor={Opos.anchor} />
      {/* etykiety kątów (pomarańczowe) */}
      <AngleLabel
        x={labelCentralX}
        y={labelCentralY}
        sub="sr"
        value={centralValue}
        color={centralColor}
      />
      <AngleLabel
        x={labelInscrX}
        y={labelInscrY}
        sub="w"
        value={inscribedValue}
        color={inscribedColor}
      />
    </svg>
  );
}
